import type { Signal } from '@angular/core'
import { computed, Injectable, signal } from '@angular/core'
import type { EntitySignalStore } from '@awork/core/state/signal-store/entitySignalStore'
import type { SignalStore } from '@awork/core/state/signal-store/signalStore'
import { PersistState } from '@awork/core/state/signal-store/persistState'
import type { Observable } from 'rxjs'
import { BehaviorSubject, delay, filter, from, map, of, switchMap, take } from 'rxjs'
import type { GenericEntity } from '@awork/core/state/signal-store/types'
import { getConfiguredPersistedStoreNames } from '@awork/core/state/signal-store/persistStateSchema'

interface ResetOptions {
  exclude: string[]
}

type StoreType<State> = SignalStore<State & object> | EntitySignalStore<State & GenericEntity>

/**
 * Service that holds all registered signal stores
 */
@Injectable({ providedIn: 'root' })
export class SignalStoreService<State = object> {
  private entityStores = new Map<string, StoreType<State>>()
  private storage: PersistState

  private restoredStores$ = new BehaviorSubject<string[]>([])
  private readonly restoredStores = signal<string[]>([])

  constructor() {
    this.initPersistState()
  }

  /**
   * Initialize the persist state storage
   */
  private initPersistState(): void {
    this.storage = PersistState.init()
  }

  /**
   * Register a store to the service
   * @param {string} storeName
   * @param {StoreType<State>} store
   */
  registerStore(storeName: string, store: StoreType<State>): void {
    this.entityStores.set(storeName, store)
  }

  /**
   * Marks a store as fully restored after hydration completed.
   * @param {string} storeName
   */
  markStoreAsRestored(storeName: string): void {
    if (this.restoredStores$.value.includes(storeName)) {
      return
    }

    this.restoredStores$.next([...this.restoredStores$.value, storeName])
    this.restoredStores.set([...this.restoredStores(), storeName])
  }

  /**
   * Get all registered stores
   * @returns {Map<string, StoreType<State>>}
   */
  getRegisteredStores(): Map<string, StoreType<State>> {
    return this.entityStores
  }

  /**
   * Resets all resettable stores
   * @param {Partial<ResetOptions>} options
   */
  resetStores(options?: Partial<ResetOptions>): void {
    this.getResettableStores(options).forEach(store => store.reset())
  }

  /**
   * Resets registered stores and deletes all configured resettable persistence before emitting.
   * @param {Partial<ResetOptions>} options Store names that must not be reset.
   * @returns {Observable<boolean>} Whether persisted store data was deleted successfully.
   */
  resetStoresAndWait(options?: Partial<ResetOptions>): Observable<boolean> {
    const resettableStores = this.getResettableStores(options)
    const registeredStoreNames = resettableStores.map(store => store.getStoreOptions().name)
    const nonResettableStoreNames = [...this.entityStores.values()]
      .filter(store => !store.getStoreOptions().resettable)
      .map(store => store.getStoreOptions().name)
    const excludedStoreNames = new Set([...(options?.exclude || []), ...nonResettableStoreNames])
    const persistedStoreNames = [...new Set([...getConfiguredPersistedStoreNames(), ...registeredStoreNames])].filter(
      storeName => !excludedStoreNames.has(storeName)
    )

    resettableStores.forEach(store => store.reset())

    if (!persistedStoreNames.length || !this.storage.storage) {
      return of(true)
    }

    return this.storage.deleteItems(persistedStoreNames)
  }

  /**
   * Gets resettable stores that are not excluded.
   * @param {Partial<ResetOptions>} options
   * @returns {StoreType<State>[]}
   */
  private getResettableStores(options?: Partial<ResetOptions>): StoreType<State>[] {
    return [...this.entityStores.values()].filter(store => {
      const storeOptions = store.getStoreOptions()

      return !!storeOptions.resettable && !options?.exclude?.includes(storeOptions.name)
    })
  }

  /**
   * Emits when the storage is initialized
   * @returns {Observable<void>}
   */
  selectOnStorageInit(): Observable<void> {
    return this.storage.onInit().pipe(delay(1), take(1))
  }

  /**
   * Determines if the storage is initialized
   * @returns {boolean}
   */
  isStorageInit(): boolean {
    return !!this.storage
  }

  /**
   * Returns the storage initialization state for startup diagnostics.
   * @returns {{ initialized: boolean; enabled: boolean }}
   */
  getStorageStatus(): { initialized: boolean; enabled: boolean } {
    return this.storage.getStatus()
  }

  /**
   * Lists registered persisted stores that have not finished restore.
   * @returns {string[]}
   */
  getPendingPersistedStoreNames(): string[] {
    return [...this.entityStores.entries()]
      .filter(([name, store]) => store.getStoreOptions().persist && !this.restoredStores().includes(name))
      .map(([name]) => name)
  }

  /**
   * Emits when a store is restored from the storage
   * @param {string} storeName
   * @returns {Observable<void>}
   */
  selectOnStoreRestored(storeName: string): Observable<void> {
    return this.restoredStores$.pipe(
      filter(restoredStores => restoredStores.includes(storeName)),
      delay(1),
      take(1),
      map(() => void 0)
    )
  }

  /**
   * Gets a signal that determines if a store is restored from the storage
   * @param {string} storeName
   * @returns {Signal<boolean>}
   */
  queryOnStoreRestored(storeName: string): Signal<boolean> {
    return computed(() => this.isStoreRestored(storeName))
  }

  selectOnStorePersisted(storeName: string): Observable<string> {
    return this.storage.onPersisted(storeName)
  }

  /**
   * Checks whether a store already completed persistence restore.
   * @param {string} storeName
   * @returns {boolean}
   */
  private isStoreRestored(storeName: string): boolean {
    return this.restoredStores().includes(storeName)
  }

  /**
   * Checks if the app storage is persisted.
   * If not, it requests permission to persist the storage.
   * The browser decides automatically if the storage can be persisted.
   * In Firefox, the user is prompted to allow it.
   * Returns false when the browser does not support storage persistence.
   * @returns {Observable<boolean>}
   */
  persistStorage(): Observable<boolean> {
    const storage = navigator.storage

    if (typeof storage?.persisted !== 'function') {
      return of(false)
    }

    return from(storage.persisted()).pipe(
      switchMap(persisted => {
        if (persisted) {
          return of(true)
        }

        return typeof storage.persist === 'function' ? from(storage.persist()) : of(false)
      })
    )
  }
}
